import axiosNest from '../../api/axiosNest';

/**
 * „Kto realizuje wg TMS” — odpowiada `/execution-department-mappings` w API
 * (odpowiedzi Marathonu/Euvic 16.10.2026, pyt. 4a i 30; przełącznik
 * `executionDepartmentHint`). Wartości pola TMS wpisuje administrator — w kodzie
 * nie ma żadnej.
 */

/** Przełącznik funkcji — klucz z rejestru API (dane, nie przemianowujemy). */
export const FEATURE_EXECUTION_HINT = 'executionDepartmentHint';

/** Działy mapowania — kody jak w słowniku stanów. */
export type ExecutionDepartment = 'FTL' | 'TRADE';

export const EXECUTION_DEPARTMENT_LABELS: Record<ExecutionDepartment, string> = {
  FTL: 'flota',
  TRADE: 'Trade',
};

export const EXECUTION_DEPARTMENT_CHOICES = [
  { id: 'FTL', name: 'Flota' },
  { id: 'TRADE', name: 'Trade' },
];

/** Klucz filtra listy zleceń — kontrakt z API (`filters.ts`, `tmsExecution`). */
export const EXECUTION_FILTER_KEY = 'tmsExecution';

export function isExecutionDepartment(v: unknown): v is ExecutionDepartment {
  return v === 'FTL' || v === 'TRADE';
}

/** Znacznik „wg TMS: Trade” — `null`, gdy brak podpowiedzi albo przełącznika. */
export function executionHintLabel(v: unknown): string | null {
  return isExecutionDepartment(v) ? `wg TMS: ${EXECUTION_DEPARTMENT_LABELS[v]}` : null;
}

export interface ExecutionMappingRow {
  id: number;
  value: string;
  department: string;
  createdByName: string | null;
  createdAt: string;
  updatedByName: string | null;
  updatedAt: string;
}

export interface ExecutionValueRow {
  value: string;
  orders: number;
  department: ExecutionDepartment | null;
}

export interface ExecutionMappingChange {
  id: number;
  mappingId: number | null;
  value: string;
  oldDepartment: string | null;
  newDepartment: string | null;
  reason: string;
  changedByName: string | null;
  changedAt: string;
}

const BASE = '/execution-department-mappings';

export async function fetchMappings(): Promise<ExecutionMappingRow[]> {
  const { data } = await axiosNest.get<ExecutionMappingRow[]>(BASE);
  return Array.isArray(data) ? data : [];
}

export async function fetchValues(): Promise<ExecutionValueRow[]> {
  const { data } = await axiosNest.get<ExecutionValueRow[]>(`${BASE}/values`);
  return Array.isArray(data) ? data : [];
}

export async function fetchMappingHistory(): Promise<ExecutionMappingChange[]> {
  const { data } = await axiosNest.get<ExecutionMappingChange[]>(`${BASE}/history`);
  return Array.isArray(data) ? data : [];
}

export async function createMapping(input: {
  value: string;
  department: ExecutionDepartment;
  reason: string;
}): Promise<void> {
  await axiosNest.post(BASE, input);
}

export async function updateMapping(
  id: number,
  input: { department: ExecutionDepartment; reason: string },
): Promise<void> {
  await axiosNest.patch(`${BASE}/${id}`, input);
}

export async function removeMapping(id: number, reason: string): Promise<void> {
  await axiosNest.delete(`${BASE}/${id}`, { data: { reason } });
}

/** Dział słownie w historii; `null` = brak (dodanie albo usunięcie). */
export function departmentText(v: string | null): string {
  if (v === null) return '—';
  return isExecutionDepartment(v) ? EXECUTION_DEPARTMENT_LABELS[v] : v;
}
